import type { CreateWasteInput, WasteCategoryOption } from '../types/waste';

// F1 · lógica PURA de la captura de merma (asistente «¿Qué se perdió? → ¿Por qué? → ¿Cuánto? →
// Nota»). Vive fuera del componente para poder probarla sin renderizador. Nada llega
// preseleccionado salvo la tienda cuando el empleado solo tiene una.

export type WasteStep = 'STORE' | 'INGREDIENT' | 'CATEGORY' | 'QUANTITY' | 'NOTE';

export interface WasteDraft {
  storeId: string;
  ingredientId: string;
  category: string;
  /** Texto tal como se escribe (acepta coma decimal); se interpreta con `parseWasteQuantity`. */
  quantity: string;
  notes: string;
}

export const MAX_WASTE_QUANTITY = 99_999_999;

export function emptyWasteDraft(storeIds: string[]): WasteDraft {
  return { storeId: storeIds.length === 1 ? storeIds[0]! : '', ingredientId: '', category: '', quantity: '', notes: '' };
}

/** Con 2 o más tiendas se pregunta; con una, se usa esa (el servidor igual la valida). */
export function asksWasteStore(storeCount: number): boolean {
  return storeCount > 1;
}

export function wasteSteps(askStore: boolean): WasteStep[] {
  return askStore ? ['STORE', 'INGREDIENT', 'CATEGORY', 'QUANTITY', 'NOTE'] : ['INGREDIENT', 'CATEGORY', 'QUANTITY', 'NOTE'];
}

export function nextWasteStep(step: WasteStep, steps: WasteStep[]): WasteStep {
  return steps[Math.min(steps.indexOf(step) + 1, steps.length - 1)]!;
}

export function previousWasteStep(step: WasteStep, steps: WasteStep[]): WasteStep {
  return steps[Math.max(steps.indexOf(step) - 1, 0)]!;
}

/**
 * true si la coma parece separador de MILES (es-MX): «1,000», «12,500». Es ambiguo —en es-MX la coma
 * es de miles, pero hay quien la usa como decimal— y adivinar mal registra 1 en vez de 1000. Se pide
 * escribirlo sin separador. «0,250» NO es ambiguo (nadie escribe miles empezando en 0): es decimal.
 */
export function looksLikeThousandsSeparator(text: string): boolean {
  return /[1-9]\d*,\d{3}(?!\d)/.test(text.trim());
}

/**
 * Número escrito por una persona: acepta punto o coma DECIMAL («0.5», «0,5») y hasta `maxDecimals`
 * decimales. Rechaza vacío, texto, notación científica, varios separadores y la coma de miles.
 */
export function parseDecimalInput(text: string, maxDecimals = 4): number | null {
  const trimmed = text.trim();
  if (looksLikeThousandsSeparator(trimmed)) return null;
  const normalized = trimmed.replace(',', '.');
  if (!new RegExp(`^\\d+(\\.\\d{1,${maxDecimals}})?$`).test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

/** «2», «0.5» o «0,5» → número; vacío, cero, negativo, texto, coma de miles o más de 4 decimales → null. */
export function parseWasteQuantity(text: string): number | null {
  const value = parseDecimalInput(text, 4);
  return value !== null && value > 0 && value <= MAX_WASTE_QUANTITY ? value : null;
}

/**
 * Botones +/−: ayuda para cantidades chicas, nunca la única forma de capturar. No baja de cero. Lo
 * escrito que no se entiende («1,000», «2.55555») NO se «arregla» en silencio: se deja igual y el
 * aviso del paso sigue a la vista.
 */
export function stepWasteQuantity(text: string, delta: 1 | -1): string {
  const trimmed = text.trim();
  const current = trimmed ? parseDecimalInput(trimmed, 4) : 0;
  if (current === null || current > MAX_WASTE_QUANTITY) return text;
  const next = Math.max(0, Math.round((current + delta) * 10_000) / 10_000);
  if (next > MAX_WASTE_QUANTITY) return text;
  return next === 0 ? '' : String(next);
}

/**
 * ¿El servidor rechazó ESTA merma de forma definitiva? Solo entonces se suelta la llave. Una sesión
 * vencida (401), un 403 ajeno a la merma, un tiempo agotado (408) o un exceso de peticiones (429) no
 * dicen nada de si el primer intento se guardó: la captura se conserva para reintentar sin duplicar.
 * El permiso de mermas tampoco, porque se revisa antes de reconocer el reintento y pudo cambiar
 * después del primer envío.
 */
export function isFinalWasteRejection(status: number, code: string | undefined): boolean {
  if (status < 400 || status >= 500 || status === 401 || status === 408 || status === 429) return false;
  if (!code || code === 'WASTE_PERMISSION_REQUIRED') return false;
  return code.startsWith('WASTE_') || code === 'VALIDATION_ERROR' || code === 'INGREDIENT_NOT_APPROVED';
}

/** Captura cuyo envío quedó en duda: se retoma con la MISMA llave para que no se duplique. */
export interface PendingWasteCapture {
  draft: WasteDraft;
  requestId: string;
}

const WASTE_DRAFT_FIELDS = ['storeId', 'ingredientId', 'category', 'quantity', 'notes'] as const;

/** Lee una captura en duda guardada en el navegador sin confiar en su forma: algo raro → null. */
export function parsePendingWaste(raw: string | null): PendingWasteCapture | null {
  if (!raw) return null;
  let value: unknown;
  try { value = JSON.parse(raw); } catch { return null; }
  if (!value || typeof value !== 'object') return null;
  const { draft, requestId } = value as { draft?: unknown; requestId?: unknown };
  if (typeof requestId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requestId)) return null;
  if (!draft || typeof draft !== 'object') return null;
  const fields = draft as Record<string, unknown>;
  if (!WASTE_DRAFT_FIELDS.every((field) => typeof fields[field] === 'string')) return null;
  const clean = Object.fromEntries(WASTE_DRAFT_FIELDS.map((field) => [field, fields[field]])) as unknown as WasteDraft;
  // Solo se guardó al enviar, con todo capturado: un borrador incompleto o una cantidad que no se
  // entiende no es una captura en duda legítima.
  if (!clean.ingredientId || !clean.category || parseWasteQuantity(clean.quantity) === null) return null;
  return { requestId, draft: clean };
}

export function wasteCategoryRequiresNote(code: string, categories: WasteCategoryOption[]): boolean {
  return categories.find((category) => category.code === code)?.requiresNote ?? false;
}

/** Motivo por el que no se puede avanzar en el paso (null = listo). */
export function wasteStepBlocker(step: WasteStep, draft: WasteDraft, ctx: { requiresNote: boolean }): string | null {
  switch (step) {
    case 'STORE': return draft.storeId ? null : 'Elige la tienda.';
    case 'INGREDIENT': return draft.ingredientId ? null : 'Elige qué se perdió.';
    case 'CATEGORY': return draft.category ? null : 'Elige por qué se perdió.';
    case 'QUANTITY':
      if (!draft.quantity.trim()) return 'Escribe cuánto se perdió.';
      if (looksLikeThousandsSeparator(draft.quantity)) return 'Escríbelo sin separador de miles: 1000, no 1,000 (para decimales usa 1.5).';
      return parseWasteQuantity(draft.quantity) === null ? 'Escribe una cantidad mayor a cero (hasta 4 decimales).' : null;
    case 'NOTE':
    default:
      return ctx.requiresNote && !draft.notes.trim() ? 'Cuando eliges «Otro», escribe qué pasó.' : null;
  }
}

/**
 * Lo que se manda al servidor. SIN fecha (la pone el servidor), sin unidad (la base del insumo), sin
 * costo. La tienda va siempre que se conozca (el servidor valida que sea una de las suyas): así una
 * captura retomada nunca cae en otra tienda si a la persona le cambiaron las suyas. `requestId` = la
 * llave de este intento: se reutiliza si el envío se reintenta, así un doble toque o un reintento no
 * duplica la merma.
 */
export function buildWastePayload(draft: WasteDraft, ctx: { requestId: string }): CreateWasteInput {
  const quantity = parseWasteQuantity(draft.quantity);
  if (quantity === null) throw new Error('Cantidad inválida');
  const notes = draft.notes.trim();
  return {
    requestId: ctx.requestId,
    ...(draft.storeId ? { storeId: draft.storeId } : {}),
    ingredientId: draft.ingredientId,
    category: draft.category,
    quantity,
    ...(notes ? { notes } : {}),
  };
}
